"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

// Paths that render completely bare (no navbar padding, no footer, no chrome)
const BARE_PATHS_EXACT = [
  "/login", "/login/", "/login/verify", "/login/verify/",
  "/signup", "/signup/",
  "/checkout", "/checkout/",
  "/billing/success", "/billing/success/",
];
const BARE_PATH_PREFIXES = ["/onboarding"];

function isPreviewPath(pathname: string | null): boolean {
  const p = pathname ?? "";
  return p === "/preview" || p.startsWith("/preview?") || p.startsWith("/preview/");
}

function isDashboardPath(pathname: string | null): boolean {
  const p = pathname ?? "";
  return p === "/dashboard" || p.startsWith("/dashboard/");
}

function isBarePath(pathname: string | null): boolean {
  const p = pathname ?? "";
  return (
    BARE_PATHS_EXACT.includes(p) ||
    BARE_PATH_PREFIXES.some(
      (prefix) => p === prefix || p.startsWith(prefix + "/") || p.startsWith(prefix + "?"),
    )
  );
}

function Footer() {
  return (
    <footer className="bg-white border-t border-border py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
          <div>
            <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3">Product</h4>
            <nav className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link href="/features/" className="hover:text-foreground transition-colors">Features</Link>
              <Link href="/how-it-works/" className="hover:text-foreground transition-colors">How it works</Link>
              <Link href="/pricing/" className="hover:text-foreground transition-colors">Pricing</Link>
              <Link href="/demo/" className="hover:text-foreground transition-colors">Demo</Link>
              <Link href="/connect/" className="hover:text-foreground transition-colors">Connect</Link>
              <Link href="/compare/" className="hover:text-foreground transition-colors">Compare</Link>
            </nav>
          </div>
          <div>
            <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3">Platforms</h4>
            <nav className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link href="/platforms/webflow/" className="hover:text-foreground transition-colors">Webflow</Link>
              <Link href="/platforms/wordpress/" className="hover:text-foreground transition-colors">WordPress</Link>
              <Link href="/platforms/drupal/" className="hover:text-foreground transition-colors">Drupal</Link>
              <Link href="/platforms/" className="hover:text-foreground transition-colors">All platforms</Link>
            </nav>
          </div>
          <div>
            <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3">Resources</h4>
            <nav className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link href="/docs/" className="hover:text-foreground transition-colors">Docs</Link>
              <Link href="/faq/" className="hover:text-foreground transition-colors">FAQ</Link>
              <Link href="/blog/" className="hover:text-foreground transition-colors">Blog</Link>
            </nav>
          </div>
          <div>
            <h4 className="text-xs font-bold text-foreground uppercase tracking-wider mb-3">Company</h4>
            <nav className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link href="/about/" className="hover:text-foreground transition-colors">About</Link>
              <Link href="/contact/" className="hover:text-foreground transition-colors">Contact</Link>
              <Link href="/investors/" className="hover:text-foreground transition-colors">Investors</Link>
            </nav>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-border">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <Link href="/">
              <picture>
                <source srcSet="/images/legible-logo-sm.webp" type="image/webp" />
                <img src="/images/legible-logo.png" alt="Legible" className="h-7 w-auto" width={97} height={28} fetchPriority="high" />
              </picture>
            </Link>
            <nav className="flex items-center gap-4 text-xs text-muted-foreground" aria-label="Legal">
              <Link href="/terms/" className="hover:text-foreground transition-colors">Terms</Link>
              <Link href="/privacy/" className="hover:text-foreground transition-colors">Privacy</Link>
            </nav>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            © 2026 Legible
          </span>
        </div>
      </div>
    </footer>
  );
}

export function SiteShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    if (window.location.pathname.endsWith("index.html")) {
      const clean = window.location.pathname.replace(/index\.html$/, "");
      window.history.replaceState(null, "", clean + window.location.search + window.location.hash);
    }
  }, []);

  if (!mounted) {
    return (
      <>
        <main className="flex-1 w-full pt-20">
          {children}
        </main>
        <Footer />
      </>
    );
  }

  // ── After mount: render the correct structure for the current path ────────────

  if (isBarePath(pathname)) {
    return <>{children}</>;
  }

  if (isPreviewPath(pathname)) {
    return <main className="flex-1 w-full pt-20">{children}</main>;
  }

  if (isDashboardPath(pathname)) {
    return <main className="flex-1 w-full pt-20">{children}</main>;
  }

  return (
    <>
      <main className="flex-1 w-full pt-20">{children}</main>
      <Footer />
    </>
  );
}
